import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Componente de bloco arrastável
function SortableBlock({ id, block, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const getBlockIcon = (type) => {
    switch(type) {
      case 'text': return '📝';
      case 'audio': return '🎤';
      case 'image': return '🖼️';
      case 'video': return '🎬';
      case 'timer': return '⏱️';
      default: return '📄';
    }
  };

  const getBlockLabel = (type) => {
    switch(type) {
      case 'text': return 'Texto';
      case 'audio': return 'Áudio';
      case 'image': return 'Imagem';
      case 'video': return 'Vídeo';
      case 'timer': return 'Temporizador';
      default: return 'Desconhecido';
    }
  };

  return (
    <div ref={setNodeRef} style={{...styles.block, ...style}}>
      {/* Área de arrastar - separada dos botões */}
      <div style={styles.dragHandle} {...attributes} {...listeners}>
        <span style={styles.dragIcon}>☰</span>
      </div>
      
      {/* Conteúdo do bloco */}
      <div style={styles.blockContent2}>
        <div style={styles.blockHeader}>
          <span style={styles.blockIcon}>{getBlockIcon(block.type)}</span>
          <span style={styles.blockType}>{getBlockLabel(block.type)}</span>
          <div style={styles.blockActions}>
            <button 
              onClick={(e) => { 
                e.stopPropagation(); 
                e.preventDefault();
                onEdit(block); 
              }} 
              style={styles.blockActionBtn}
              type="button"
            >
              ✏️
            </button>
            <button 
              onClick={(e) => { 
                e.stopPropagation(); 
                e.preventDefault();
                onDelete(id); 
              }} 
              style={styles.blockActionBtn}
              type="button"
            >
              🗑️
            </button>
          </div>
        </div>
        <div style={styles.blockInfo}>
          {block.type === 'text' && <p>{block.content?.substring(0, 100) || 'Texto vazio'}...</p>}
          {block.type === 'audio' && <p>📎 {block.filename || 'Nenhum arquivo'}</p>}
          {block.type === 'image' && (
            <p>📎 {block.filename || 'Nenhum arquivo'}
              {block.caption && ` - "${block.caption.substring(0, 50)}..."`}
            </p>
          )}
          {block.type === 'video' && (
            <p>📎 {block.filename || 'Nenhum arquivo'}
              {block.caption && ` - "${block.caption.substring(0, 50)}..."`}
            </p>
          )}
          {block.type === 'timer' && <p>⏱️ Aguardar: {(block.delay || 5000) / 1000}s</p>}
        </div>
      </div>
    </div>
  );
}

function FlowEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [flowName, setFlowName] = useState('');
  const [flowDescription, setFlowDescription] = useState('');
  const [flowTrigger, setFlowTrigger] = useState('1');
  const [blocks, setBlocks] = useState([]);
  const [editingBlock, setEditingBlock] = useState(null);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [newBlockType, setNewBlockType] = useState('text');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    const storedUser = localStorage.getItem('zaptime_user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    
    if (id) {
      loadFlow();
    }
  }, [id]);

  const getUserId = () => {
    const user = JSON.parse(localStorage.getItem('zaptime_user') || '{}');
    return user.id;
  };

  const getHeaders = () => ({
    'Content-Type': 'application/json',
    'X-User-Id': getUserId()
  });

  const loadFlow = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/flows/${id}`, {
        headers: getHeaders()
      });
      const data = await response.json();
      
      if (data.success && data.flow) {
        setFlowName(data.flow.name);
        setFlowDescription(data.flow.description || '');
        setFlowTrigger(data.flow.trigger || '1');
        setBlocks(data.flow.blocks || []);
      }
    } catch (error) {
      console.error('Erro ao carregar fluxo:', error);
      alert('Erro ao carregar fluxo');
    } finally {
      setLoading(false);
    }
  };

  // UPLOAD DE ARQUIVO - CORRIGIDO para enviar type via query params
  const handleFileUpload = async (file, type) => {
    if (!file) return;

    setUploading(true);
    
    const formData = new FormData();
    formData.append('file', file);
    // NÃO adicionar type no FormData

    try {
      // CORREÇÃO: Enviar type via query params ao invés de FormData
      const response = await fetch(`/api/upload?type=${type}`, {
        method: 'POST',
        body: formData
        // NÃO enviar Content-Type header, deixar o browser definir com boundary
      });

      const data = await response.json();
      
      if (data.success) {
        // Atualizar filename no bloco sendo editado
        setEditingBlock(prev => ({
          ...prev,
          filename: data.filename
        }));
        alert(`✅ Arquivo enviado: ${data.filename}`);
      } else {
        alert('❌ Erro ao enviar arquivo: ' + data.message);
      }
    } catch (error) {
      console.error('Erro no upload:', error);
      alert('❌ Erro ao enviar arquivo');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!flowName.trim()) {
      alert('Nome do fluxo é obrigatório');
      return;
    }

    if (blocks.length === 0) {
      alert('Adicione pelo menos um bloco ao fluxo');
      return;
    }

    setLoading(true);
    
    const flowData = {
      name: flowName,
      description: flowDescription,
      trigger: flowTrigger,
      blocks: blocks,
      active: false
    };

    try {
      const url = id ? `/api/flows/${id}` : '/api/flows';
      const method = id ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: getHeaders(),
        body: JSON.stringify(flowData)
      });

      const data = await response.json();
      
      if (data.success) {
        alert(`✅ Fluxo ${id ? 'atualizado' : 'criado'} com sucesso!`);
        
        // Recarregar fluxo se for o ativo
        try {
          await fetch('/api/flow/reload', {
            method: 'POST',
            headers: getHeaders()
          });
        } catch (e) {
          console.log('Fluxo salvo, mas não recarregado (pode não estar ativo)');
        }
        
        navigate('/flows');
      } else {
        alert('❌ Erro: ' + (data.message || 'Erro ao salvar fluxo'));
      }
    } catch (error) {
      console.error('Erro ao salvar fluxo:', error);
      alert('❌ Erro ao salvar fluxo');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBlock = (type) => {
    // CORREÇÃO: Recebe tipo como parâmetro e fecha modal primeiro
    setShowBlockModal(false);
    setEditingBlock(null); // Limpa bloco anterior
    
    // Aguardar um momento para garantir que fechou e resetou o estado
    setTimeout(() => {
      const newBlock = {
        id: `block-${Date.now()}`,
        type: type, // Usa o parâmetro, não o state
        content: '',
        filename: '',
        caption: '',
        delay: 5000
      };
      
      setNewBlockType(type); // Atualiza o state também
      setEditingBlock(newBlock);
      setShowBlockModal(true);
    }, 100); // Aumenta para 100ms para garantir reset completo
  };

  const handleEditBlock = (block) => {
    setEditingBlock({...block});
    setNewBlockType(block.type);
    setShowBlockModal(true);
  };

  const handleSaveBlock = () => {
    if (!editingBlock) return;

    // Validações por tipo
    if (editingBlock.type === 'text' && !editingBlock.content?.trim()) {
      alert('O texto não pode estar vazio');
      return;
    }

    if ((editingBlock.type === 'audio' || editingBlock.type === 'video' || editingBlock.type === 'image') && !editingBlock.filename?.trim()) {
      alert('Você precisa fazer upload de um arquivo');
      return;
    }

    if (editingBlock.type === 'timer' && (!editingBlock.delay || editingBlock.delay < 1000)) {
      alert('O timer deve ser de pelo menos 1000ms (1 segundo)');
      return;
    }

    const existingIndex = blocks.findIndex(b => b.id === editingBlock.id);
    
    if (existingIndex >= 0) {
      // Editando bloco existente
      const newBlocks = [...blocks];
      newBlocks[existingIndex] = editingBlock;
      setBlocks(newBlocks);
    } else {
      // Novo bloco
      setBlocks([...blocks, editingBlock]);
    }

    setShowBlockModal(false);
    setEditingBlock(null);
  };

  const handleDeleteBlock = (blockId) => {
    if (window.confirm('Tem certeza que deseja deletar este bloco?')) {
      setBlocks(blocks.filter(b => b.id !== blockId));
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setBlocks((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('zaptime_user');
    localStorage.removeItem('zaptime_token');
    navigate('/login');
  };

  if (loading && !showBlockModal) {
    return <div style={styles.loading}>Carregando...</div>;
  }

  const isAdmin = user?.username === 'admin';

  return (
    <div style={styles.container}>
      {/* HEADER */}
      <header style={styles.header}>
        <h1 style={styles.title}>🚀 ZappExpress</h1>
        <div style={styles.userInfo}>
          <span style={styles.userName}>
            👤 {user?.username}
            {isAdmin && <span style={styles.adminBadge}>ADMIN</span>}
          </span>
          <button onClick={handleLogout} style={styles.logoutBtn}>
            Sair
          </button>
        </div>
      </header>

      {/* MENU */}
      <nav style={styles.nav}>
        <Link to="/" style={styles.navLink}>🏠 Dashboard</Link>
        <Link to="/flows" style={styles.navLink}>📋 Meus Fluxos</Link>
        {isAdmin && (
          <Link to="/users" style={styles.navLink}>👥 Usuários</Link>
        )}
      </nav>

      {/* CONTEÚDO */}
      <div style={styles.content}>
        <div style={styles.pageHeader}>
          <h2>{id ? '✏️ Editar Fluxo' : '➕ Novo Fluxo'}</h2>
          <div style={styles.headerActions}>
            <button onClick={() => navigate('/flows')} style={styles.cancelButton}>
              ❌ Cancelar
            </button>
            <button onClick={handleSave} disabled={loading} style={styles.saveButton}>
              {loading ? '💾 Salvando...' : '💾 Salvar Fluxo'}
            </button>
          </div>
        </div>

        {/* INFORMAÇÕES DO FLUXO */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>📝 Informações do Fluxo</h3>
          
          <div style={styles.formGroup}>
            <label style={styles.label}>Nome do Fluxo:</label>
            <input
              type="text"
              value={flowName}
              onChange={(e) => setFlowName(e.target.value)}
              placeholder="Ex: Boas-vindas"
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Descrição (opcional):</label>
            <textarea
              value={flowDescription}
              onChange={(e) => setFlowDescription(e.target.value)}
              placeholder="Descreva o objetivo deste fluxo..."
              style={styles.textarea}
              rows="3"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Gatilho (palavra que ativa o fluxo):</label>
            <input
              type="text"
              value={flowTrigger}
              onChange={(e) => setFlowTrigger(e.target.value)}
              placeholder="Ex: oi, menu, ajuda"
              style={styles.input}
            />
            <small style={styles.hint}>
              💡 Quando o cliente enviar esta palavra, o fluxo será executado
            </small>
          </div>
        </div>

        {/* BLOCOS DO FLUXO */}
        <div style={styles.card}>
          <div style={styles.cardHeader2}>
            <h3 style={styles.cardTitle}>🧩 Blocos do Fluxo</h3>
            <div style={styles.blockTypeButtons}>
              <button onClick={() => handleAddBlock('text')} style={{...styles.addBlockBtn, ...styles.btnText}}>
                📝 Texto
              </button>
              <button onClick={() => handleAddBlock('audio')} style={{...styles.addBlockBtn, ...styles.btnAudio}}>
                🎤 Áudio
              </button>
              <button onClick={() => handleAddBlock('image')} style={{...styles.addBlockBtn, ...styles.btnImage}}>
                🖼️ Imagem
              </button>
              <button onClick={() => handleAddBlock('video')} style={{...styles.addBlockBtn, ...styles.btnVideo}}>
                🎬 Vídeo
              </button>
              <button onClick={() => handleAddBlock('timer')} style={{...styles.addBlockBtn, ...styles.btnTimer}}>
                ⏱️ Timer
              </button>
            </div>
          </div>

          {blocks.length === 0 ? (
            <div style={styles.emptyBlocks}>
              <p>📋 Nenhum bloco adicionado ainda.</p>
              <p>Clique em um dos botões acima para começar!</p>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={blocks.map(b => b.id)}
                strategy={verticalListSortingStrategy}
              >
                <div style={styles.blocksList}>
                  {blocks.map((block, index) => (
                    <div key={block.id} style={styles.blockWrapper}>
                      <span style={styles.blockIndex}>{index + 1}</span>
                      <SortableBlock
                        id={block.id}
                        block={block}
                        onEdit={handleEditBlock}
                        onDelete={handleDeleteBlock}
                      />
                    </div>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>

      {/* MODAL DE EDIÇÃO DE BLOCO */}
      {showBlockModal && editingBlock && (
        <div style={styles.modalOverlay} onClick={() => setShowBlockModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>
              {editingBlock.id.startsWith('block-') && blocks.find(b => b.id === editingBlock.id) ? 'Editar' : 'Adicionar'} Bloco: {editingBlock.type === 'text' ? '📝 Texto' : editingBlock.type === 'audio' ? '🎤 Áudio' : editingBlock.type === 'image' ? '🖼️ Imagem' : editingBlock.type === 'video' ? '🎬 Vídeo' : '⏱️ Timer'}
            </h3>

            <div style={styles.modalContent}>
              {/* TEXTO */}
              {editingBlock.type === 'text' && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Mensagem de Texto:</label>
                  <textarea
                    value={editingBlock.content || ''}
                    onChange={(e) => setEditingBlock({...editingBlock, content: e.target.value})}
                    placeholder="Digite a mensagem que será enviada..."
                    style={styles.textarea}
                    rows="6"
                  />
                </div>
              )}

              {/* ÁUDIO */}
              {editingBlock.type === 'audio' && (
                <>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Arquivo de Áudio:</label>
                    <div style={styles.uploadArea}>
                      <input
                        type="file"
                        accept="audio/*,.mp3,.ogg,.wav,.m4a"
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            handleFileUpload(e.target.files[0], 'audio');
                          }
                        }}
                        style={styles.fileInput}
                        id="audioFile"
                      />
                      <label htmlFor="audioFile" style={styles.fileLabel}>
                        {uploading ? '📤 Enviando...' : '📁 Escolher Arquivo de Áudio'}
                      </label>
                    </div>
                    {editingBlock.filename && (
                      <div style={styles.fileInfo}>
                        ✅ Arquivo: <strong>{editingBlock.filename}</strong>
                      </div>
                    )}
                    <small style={styles.hint}>
                      Formatos: MP3, OGG, WAV, M4A
                    </small>
                  </div>
                </>
              )}

              {/* IMAGEM */}
              {editingBlock.type === 'image' && (
                <>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Arquivo de Imagem:</label>
                    <div style={styles.uploadArea}>
                      <input
                        type="file"
                        accept="image/*,.jpg,.jpeg,.png,.gif,.webp"
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            handleFileUpload(e.target.files[0], 'image');
                          }
                        }}
                        style={styles.fileInput}
                        id="imageFile"
                      />
                      <label htmlFor="imageFile" style={styles.fileLabel}>
                        {uploading ? '📤 Enviando...' : '📁 Escolher Imagem'}
                      </label>
                    </div>
                    {editingBlock.filename && (
                      <div style={styles.fileInfo}>
                        ✅ Arquivo: <strong>{editingBlock.filename}</strong>
                      </div>
                    )}
                    <small style={styles.hint}>
                      Formatos: JPG, PNG, GIF, WEBP
                    </small>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Legenda (opcional):</label>
                    <textarea
                      value={editingBlock.caption || ''}
                      onChange={(e) => setEditingBlock({...editingBlock, caption: e.target.value})}
                      placeholder="Texto que acompanha a imagem..."
                      style={styles.textarea}
                      rows="3"
                    />
                  </div>
                </>
              )}

              {/* VÍDEO */}
              {editingBlock.type === 'video' && (
                <>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Arquivo de Vídeo:</label>
                    <div style={styles.uploadArea}>
                      <input
                        type="file"
                        accept="video/*,.mp4,.avi,.mov,.mkv"
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            handleFileUpload(e.target.files[0], 'video');
                          }
                        }}
                        style={styles.fileInput}
                        id="videoFile"
                      />
                      <label htmlFor="videoFile" style={styles.fileLabel}>
                        {uploading ? '📤 Enviando...' : '📁 Escolher Vídeo'}
                      </label>
                    </div>
                    {editingBlock.filename && (
                      <div style={styles.fileInfo}>
                        ✅ Arquivo: <strong>{editingBlock.filename}</strong>
                      </div>
                    )}
                    <small style={styles.hint}>
                      Formatos: MP4, AVI, MOV, MKV
                    </small>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Legenda (opcional):</label>
                    <textarea
                      value={editingBlock.caption || ''}
                      onChange={(e) => setEditingBlock({...editingBlock, caption: e.target.value})}
                      placeholder="Texto que acompanha o vídeo..."
                      style={styles.textarea}
                      rows="3"
                    />
                  </div>
                </>
              )}

              {/* TIMER */}
              {editingBlock.type === 'timer' && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Tempo de Espera (milissegundos):</label>
                  <input
                    type="number"
                    value={editingBlock.delay || 5000}
                    onChange={(e) => setEditingBlock({...editingBlock, delay: parseInt(e.target.value) || 5000})}
                    min="1000"
                    step="1000"
                    style={styles.input}
                  />
                  <small style={styles.hint}>
                    💡 {((editingBlock.delay || 5000) / 1000)} segundos de pausa antes do próximo bloco
                    <br />
                    Mínimo: 1000ms (1 segundo)
                  </small>
                </div>
              )}
            </div>

            <div style={styles.modalActions}>
              <button onClick={handleSaveBlock} style={styles.modalSaveBtn} disabled={uploading}>
                ✅ Salvar Bloco
              </button>
              <button onClick={() => setShowBlockModal(false)} style={styles.modalCancelBtn}>
                ❌ Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#1a1a1a'
  },
  header: {
    backgroundColor: '#2c3e50',
    color: 'white',
    padding: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
  },
  title: {
    margin: 0,
    fontSize: '24px'
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px'
  },
  userName: {
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  adminBadge: {
    backgroundColor: '#3498db',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 'bold'
  },
  logoutBtn: {
    padding: '8px 16px',
    backgroundColor: '#e74c3c',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold'
  },
  nav: {
    backgroundColor: '#34495e',
    padding: '15px 20px',
    display: 'flex',
    gap: '20px'
  },
  navLink: {
    color: 'white',
    textDecoration: 'none',
    fontSize: '16px',
    padding: '8px 16px',
    borderRadius: '4px',
    transition: 'background-color 0.3s'
  },
  content: {
    padding: '30px',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
    color: '#fff'
  },
  headerActions: {
    display: 'flex',
    gap: '10px'
  },
  saveButton: {
    padding: '12px 24px',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold'
  },
  cancelButton: {
    padding: '12px 24px',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold'
  },
  card: {
    backgroundColor: '#2a2a2a',
    border: '1px solid #444',
    borderRadius: '12px',
    padding: '25px',
    marginBottom: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
  },
  cardTitle: {
    marginTop: 0,
    marginBottom: '20px',
    color: '#fff',
    fontSize: '20px'
  },
  cardHeader2: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '15px'
  },
  formGroup: {
    marginBottom: '20px'
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    color: '#bbb',
    fontWeight: 'bold',
    fontSize: '14px'
  },
  input: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '1px solid #555',
    borderRadius: '6px',
    backgroundColor: '#1a1a1a',
    color: '#fff',
    outline: 'none'
  },
  textarea: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '1px solid #555',
    borderRadius: '6px',
    backgroundColor: '#1a1a1a',
    color: '#fff',
    outline: 'none',
    fontFamily: 'inherit',
    resize: 'vertical'
  },
  hint: {
    display: 'block',
    marginTop: '5px',
    fontSize: '13px',
    color: '#999'
  },
  blockTypeButtons: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap'
  },
  addBlockBtn: {
    padding: '10px 16px',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    color: 'white',
    transition: 'opacity 0.3s'
  },
  btnText: {
    backgroundColor: '#007bff'
  },
  btnAudio: {
    backgroundColor: '#28a745'
  },
  btnImage: {
    backgroundColor: '#ffc107',
    color: '#000'
  },
  btnVideo: {
    backgroundColor: '#dc3545'
  },
  btnTimer: {
    backgroundColor: '#6c757d'
  },
  emptyBlocks: {
    textAlign: 'center',
    padding: '40px',
    color: '#999',
    fontSize: '16px'
  },
  blocksList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  blockWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  blockIndex: {
    backgroundColor: '#34495e',
    color: '#fff',
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 'bold',
    flexShrink: 0
  },
  block: {
    flex: 1,
    backgroundColor: '#34495e',
    border: '1px solid #555',
    borderRadius: '8px',
    padding: '15px',
    display: 'flex',
    gap: '15px',
    alignItems: 'center',
    cursor: 'move'
  },
  dragHandle: {
    cursor: 'grab',
    padding: '10px',
    color: '#999',
    fontSize: '20px',
    userSelect: 'none',
    display: 'flex',
    alignItems: 'center'
  },
  dragIcon: {
    display: 'block'
  },
  blockContent2: {
    flex: 1
  },
  blockHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '8px'
  },
  blockIcon: {
    fontSize: '20px'
  },
  blockType: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: '16px',
    flex: 1
  },
  blockActions: {
    display: 'flex',
    gap: '8px'
  },
  blockActionBtn: {
    padding: '6px 10px',
    backgroundColor: '#2c3e50',
    border: '1px solid #555',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'background-color 0.3s'
  },
  blockInfo: {
    color: '#bbb',
    fontSize: '14px',
    lineHeight: '1.4'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: '#2a2a2a',
    border: '1px solid #444',
    borderRadius: '12px',
    padding: '30px',
    maxWidth: '600px',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto'
  },
  modalTitle: {
    marginTop: 0,
    marginBottom: '25px',
    color: '#fff',
    fontSize: '22px'
  },
  modalContent: {
    marginBottom: '25px'
  },
  modalActions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end'
  },
  modalSaveBtn: {
    padding: '12px 24px',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold'
  },
  modalCancelBtn: {
    padding: '12px 24px',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold'
  },
  uploadArea: {
    marginBottom: '10px'
  },
  fileInput: {
    display: 'none'
  },
  fileLabel: {
    display: 'inline-block',
    padding: '12px 24px',
    backgroundColor: '#3498db',
    color: 'white',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    textAlign: 'center',
    transition: 'background-color 0.3s'
  },
  fileInfo: {
    padding: '10px',
    backgroundColor: '#1a1a1a',
    border: '1px solid #28a745',
    borderRadius: '6px',
    color: '#28a745',
    fontSize: '14px',
    marginTop: '10px'
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    fontSize: '20px',
    color: '#ccc',
    backgroundColor: '#1a1a1a'
  }
};

export default FlowEditor;